import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { RelatedGuide } from "../../../components/RelatedGuide/RelatedGuide";
import { AITaggingMock } from "../../../components/Mockups/AITaggingMock";

const AITagging = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.aiTagging.seo.title")}
        description={t("featurePages.aiTagging.seo.description")}
        keywords={t("featurePages.aiTagging.seo.keywords")}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.aiTagging.seo.title"),
            description: t("featurePages.aiTagging.seo.description"),
            url: "https://onbookmarks.com/features/ai-tagging/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "AI-powered automatic tagging",
                "Content-aware categorization",
                "Smart collection suggestions",
                "Learning from user preferences",
                "Consistent taxonomy maintenance"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free plan includes AI suggestions"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "AI Tagging",
                  item: "https://onbookmarks.com/features/ai-tagging/"
                }
              ]
            }
          }),
        }}
      />

      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("home.aiTagging.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.aiTagging.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.aiTagging.hero.description")}
                </p>
                <div>
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.aiTagging.hero.cta")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <AITaggingMock />
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.aiTagging.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.aiTagging.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-clock-history fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.aiTagging.problem.manual.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.aiTagging.problem.manual.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-shuffle fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.aiTagging.problem.inconsistent.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.aiTagging.problem.inconsistent.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-tags fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.aiTagging.problem.forgotten.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.aiTagging.problem.forgotten.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.aiTagging.solution.badge")}
                </span>
              </div>
              <AITaggingMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.aiTagging.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.aiTagging.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.aiTagging.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.aiTagging.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.aiTagging.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.aiTagging.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.aiTagging.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.aiTagging.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.aiTagging.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.aiTagging.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.aiTagging.howItWorks.step1.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.aiTagging.howItWorks.step1.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.aiTagging.howItWorks.step2.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.aiTagging.howItWorks.step2.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.aiTagging.howItWorks.step3.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.aiTagging.howItWorks.step3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.aiTagging.useCases.title")}
              </h2>
              <p className="lead text-muted mb-5">
                {t("featurePages.aiTagging.useCases.description")}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-newspaper fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.aiTagging.useCases.content.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.aiTagging.useCases.content.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.content.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.content.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.content.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-lightbulb fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">
                      {t("featurePages.aiTagging.useCases.learning.title")}
                    </h3>
                  </div>
                  <p className="text-muted mb-3">
                    {t("featurePages.aiTagging.useCases.learning.description")}
                  </p>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.learning.example1")}
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.learning.example2")}
                    </li>
                    <li>
                      <i className="bi bi-arrow-right text-purple me-2"></i>
                      {t("featurePages.aiTagging.useCases.learning.example3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedGuide
        to="/blog/automatically-tag-bookmarks"
        title="How to Automatically Tag Posts and Pages You Save"
        description="Why manual tagging fails, and how content-based AI tags fix it, for new saves and your existing pile."
      />

      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.aiTagging.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.aiTagging.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.aiTagging.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AITagging;